import React, { FC, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  FormProps,
  InputNumber,
  notification,
  Row,
  Tag,
  Typography,
} from 'antd'
import RSelectUnits from '../../../components/RSelectUnits'
import CardTitleWithButton from './CardTitleWithButton'
import Icon from '../../../components/Icon'
import RSaveOverlay from '../../../components/RSaveOverlay'
import { IStorageSystem, IStorageSystemForm } from '../../../types/storageSystem'
import { storageApi } from '../../../api'

interface Props extends FormProps {
  displayOverlayTriggerButton?: boolean
  initialValues?: IStorageSystem
}

const StorageForm: FC<Props> = ({
  initialValues,
  displayOverlayTriggerButton = false,
  ...formProps
}) => {
  const [form] = Form.useForm()
  const [savedStorageSystems, setSavedStorageSystems] = useState<IStorageSystem[]>([])
  const [ratedEnergy, setRatedEnergy] = useState(initialValues?.ratedEnergyKw)
  const [ratedPower, setRatedPower] = useState(initialValues?.ratedPowerKw)
  const formInitialValues = initialValues || { rateCapabilities: [{}], dodCycle: [{}] }

  async function fetchSotageSystems() {
    const storageSystems = await storageApi.fetchStorageSystems()
    setSavedStorageSystems(Array.isArray(storageSystems) ? storageSystems : [])
  }
  async function saveStorageSystem(saveName: string) {
    try {
      const values: IStorageSystemForm = form.getFieldsValue()
      await storageApi.createStorageSystems({ ...values, name: saveName })
      notification.success({ message: 'Storage System has been saved successfully' })
    } finally {
      fetchSotageSystems()
    }
  }

  function loadStorageSystemData(savedStorageSystemIdx: number) {
    const savedStorageSystem = savedStorageSystems[savedStorageSystemIdx]
    form.setFieldsValue(savedStorageSystem)
    setRatedPower(savedStorageSystem.ratedPowerKw)

    if (savedStorageSystem.ratedEnergyKw) {
      onChangeRatedEnergy(savedStorageSystem.ratedEnergyKw)
    } else {
      onChangeRatedEnergyParameter()
    }

    formProps.onValuesChange?.(form.getFieldsValue(), form.getFieldsValue())
  }

  function appendToArrayField(fieldName: string) {
    const curValue = form.getFieldValue(fieldName) || []
    form.setFieldsValue({ [fieldName]: [...curValue, {}] })
  }

  function onChangeRatedEnergy(val: number) {
    setRatedEnergy(val)
    form.setFieldsValue({ internalBatteryVoltageV: undefined, ratedCapacityAh: undefined })
  }
  function onChangeRatedEnergyParameter() {
    const { ratedCapacityAh, internalBatteryVoltageV } = form.getFieldsValue()
    const calculatedRatedEnergy = (ratedCapacityAh || 0) * (internalBatteryVoltageV || 0)
    form.setFieldsValue({ ratedEnergyKw: calculatedRatedEnergy })
    setRatedEnergy(calculatedRatedEnergy)
  }
  function getEnergyRatio() {
    if (!ratedEnergy) return (0.0).toFixed(2)
    const p = ratedPower || 0
    const e = ratedEnergy || 0
    return ((100 * p) / e).toFixed(2)
  }

  function onChangeRoundTripEfficiency() {
    form.setFieldsValue({
      inverterEfficiency: '',
      powerConversionUnitEfficiency: '',
      batteryRoundTripEfficiency: '',
    })
  }
  function onChangeRoundTripEfficiencyParameter() {
    const { inverterEfficiency, powerConversionUnitEfficiency, batteryRoundTripEfficiency } =
      form.getFieldsValue()
    const calculatedRoundTripEfficiency = (
      ((inverterEfficiency || 0) *
        (powerConversionUnitEfficiency || 0) *
        (batteryRoundTripEfficiency || 0)) /
      (100 * 100)
    ).toFixed(2) // %
    form.setFieldsValue({ netOverallRoundTripEfficiency: calculatedRoundTripEfficiency })
  }

  useEffect(() => {
    fetchSotageSystems()
  }, [])

  return (
    <RSaveOverlay
      displayOverlayTriggerButton={displayOverlayTriggerButton}
      loadDataOptions={savedStorageSystems.map((storageSystem, idx) => ({
        label: storageSystem.name,
        value: idx,
      }))}
      onLoadData={(savedStorageSystemIdx) => loadStorageSystemData(savedStorageSystemIdx)}
      onSave={saveStorageSystem}
    >
      <Form form={form} layout='vertical' initialValues={formInitialValues} {...formProps}>
        <Card className='ticket-primary' title='Battery Capability'>
          <div className='mb-24'>
            <Typography.Title level={5}>Battery Basics</Typography.Title>
            <Typography.Text type='secondary' italic>
              Basic characteristics of the battery such as battery investment cost, rated power and
              rated energy.
            </Typography.Text>
          </div>

          <Form.Item label='Battery Replacement' name='batteryReplacementDollars'>
            <InputNumber placeholder='(i.e. CAPEX)' addonAfter='$' />
          </Form.Item>

          <Form.Item label='Rated Power' name='ratedPowerKw'>
            <InputNumber
              addonAfter={<RSelectUnits />}
              onChange={(val: number) => setRatedPower(val)}
            />
          </Form.Item>

          <Row align='middle' gutter={10} wrap={false}>
            <Col flex={1}>
              <Form.Item label='Rated Capacity' name='ratedCapacityAh'>
                <InputNumber
                  className='w-100'
                  addonAfter='Ah'
                  onChange={onChangeRatedEnergyParameter}
                />
              </Form.Item>
            </Col>
            <Col span='auto'>
              <span>x</span>
            </Col>

            <Col flex={1}>
              <Form.Item label='Internal Battery Voltage' name='internalBatteryVoltageV'>
                <InputNumber
                  className='w-100'
                  addonAfter='V'
                  onChange={onChangeRatedEnergyParameter}
                />
              </Form.Item>
            </Col>

            <Col span='auto'>
              <span>=</span>
            </Col>

            <Col flex={1}>
              <Form.Item
                label='Rated Energy'
                tooltip='Manual change in this field triggers change in Rated Capacity and Internal Battery Voltage'
                name='ratedEnergyKw'
              >
                <InputNumber className='w-100' addonAfter='MWh' onChange={onChangeRatedEnergy} />
              </Form.Item>
            </Col>
          </Row>

          <Row justify='end'>
            <Tag color='blue'>
              Energy Ratio: <b>{getEnergyRatio()}%</b>
            </Tag>
          </Row>

          <Divider />

          <div className='mb-24'>
            <Typography.Title level={5}>SOC Constraints</Typography.Title>
            <Typography.Text type='secondary' italic>
              Parameters related with the state of charge constraints of the storage system.
            </Typography.Text>
          </div>

          <Row align='middle' gutter={40} wrap={false}>
            <Col span={12}>
              <Form.Item label='Max Allowed State of Charge (SOC)' name='maxAllowedStateOfCharge'>
                <InputNumber className='w-100' addonAfter='%' />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label='Min Allowed State of Charge (SOC)' name='minAllowedStateOfCharge'>
                <InputNumber className='w-100' addonAfter='%' />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <div className='mb-24'>
            <Typography.Title level={5}>Efficiency Parameters</Typography.Title>
            <Typography.Text type='secondary' italic>
              Distinct or combined system efficiency parameters.
            </Typography.Text>
          </div>

          <Row align='middle' gutter={10} wrap={false}>
            <Col span={12}>
              <Form.Item label='Battery Round Trip Efficiency' name='batteryRoundTripEfficiency'>
                <InputNumber
                  className='w-100'
                  addonAfter='%'
                  onChange={onChangeRoundTripEfficiencyParameter}
                />
              </Form.Item>

              <Form.Item
                label='Power Conversion Unit Efficiency'
                name='powerConversionUnitEfficiency'
              >
                <InputNumber
                  className='w-100'
                  addonAfter='%'
                  onChange={onChangeRoundTripEfficiencyParameter}
                />
              </Form.Item>

              <Form.Item label='Inverter Efficiency' name='inverterEfficiency'>
                <InputNumber
                  className='w-100'
                  addonAfter='%'
                  onChange={onChangeRoundTripEfficiencyParameter}
                />
              </Form.Item>
            </Col>

            <Col span={'auto'}>{'='}</Col>

            <Col flex={1}>
              <Form.Item
                label='Net Overall Round-Trip Efficiency'
                tooltip='Manual change in this field triggers change in Battery Round Trip Efficiency, Power Conversion Unit Efficiency and Inverter Efficiency'
                name='netOverallRoundTripEfficiency'
              >
                <InputNumber
                  className='w-100'
                  addonAfter='%'
                  onChange={onChangeRoundTripEfficiency}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          className='ticket-orange my-24'
          title={
            <CardTitleWithButton
              title='Rate Capability'
              buttonText='+ Add Point'
              onClick={() => appendToArrayField('rateCapabilities')}
            />
          }
        >
          <Typography.Text type='secondary' italic>
            Storage systems charge speed vs. efficiency and charge/depletion time.
            <br />
            <br />
            For higher C rates, not all batteries can maintain the same stored energy because of
            dissipated energy such as heat during high or low discharge.
            <br />
            <br />- A high power application would benefit from a high Power: Energy ratio battery
            <br />- A high energy application would benefit from a low Power: Energy ratio battery
          </Typography.Text>

          <Row className='mt-24' gutter={20}>
            <Col span={12}>
              <Form.Item label='Max Allowed C-Rate' name={'maxAllowedCRateForCharging'}>
                <InputNumber className='w-100' />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label='Min Allowed C-Rate' name='maxAllowedCRateForDischarging'>
                <InputNumber className='w-100' />
              </Form.Item>
            </Col>
          </Row>
          <Form.List name='rateCapabilities'>
            {(fields, { remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} align='bottom' gutter={10} wrap={false}>
                    <Col flex={1}>
                      <Form.Item label='C rate' {...restField} name={[name, 'cRate']}>
                        <InputNumber className='w-100' />
                      </Form.Item>
                    </Col>
                    <Col flex={1}>
                      <Form.Item
                        {...restField}
                        label='Depletion Time'
                        name={[name, 'depletionTime']}
                      >
                        <InputNumber className='w-100' />
                      </Form.Item>
                    </Col>
                    <Col flex={1}>
                      <Form.Item {...restField} label='Cycle Life' name={[name, 'cycleLife']}>
                        <InputNumber className='w-100' />
                      </Form.Item>
                    </Col>
                    <Col flex={1}>
                      <Form.Item
                        {...restField}
                        tooltip={
                          <span>
                            Round-Trip Efficiency for that <br />
                            C-Rate
                          </span>
                        }
                        label='Battery Round-Trip Efficiency'
                        name={[name, 'batteryRoundTripEfficiency']}
                      >
                        <InputNumber className='w-100' addonAfter='%' />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Form.Item>
                        <Button
                          icon={<Icon icon='trash-alt' margin={false} />}
                          danger
                          onClick={() => remove(name)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </Form.List>
        </Card>

        <Card
          className='ticket-green mt-24'
          title={
            <CardTitleWithButton
              title='Cycle Life'
              buttonText='+ Add Point'
              onClick={() => appendToArrayField('dodCycle')}
            />
          }
        >
          <Typography.Text type='secondary' italic>
            Storage system lifetime parameters.
            <br />
            <b>Note: Higher DoD means lower cycle life in general.</b>
          </Typography.Text>
          <Row className='mt-24' gutter={20}>
            <Col span={8}>
              <Form.Item
                label='Calendar Aging'
                tooltip='Suggested when expected cycle depths and current rates are low or operational periods are shorter than the idle periods'
                name='calendarAging'
              >
                <InputNumber className='w-100' />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label='Grid Power Constraint' name='gridPowerConstraintKw'>
                <InputNumber className='w-100' addonAfter={<RSelectUnits />} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label='Self-Consumption' name='selfConsumptionKwPerHour'>
                <InputNumber
                  className='w-100'
                  addonAfter={<RSelectUnits units={['MW/h', 'KW/h']} />}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.List name='dodCycle'>
            {(fields, { remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} align='bottom' gutter={10} wrap={false}>
                    <Col flex={1}>
                      <Form.Item label='DoD' {...restField} name={[name, 'dod']}>
                        <InputNumber className='w-100' addonAfter='%' />
                      </Form.Item>
                    </Col>
                    <Col flex={1}>
                      <Form.Item label='Cycle Number' {...restField} name={[name, 'cycleNumber']}>
                        <InputNumber className='w-100' />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Form.Item>
                        <Button
                          icon={<Icon icon='trash-alt' margin={false} />}
                          danger
                          onClick={() => remove(name)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </Form.List>
        </Card>
      </Form>
    </RSaveOverlay>
  )
}

export default StorageForm
